var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { accountBadgeDistributionStatuses, accountBadgeEntityName, getAccountBadgeValidDistributionStatusChanges, logBadgeHistoryActionFunctionDef, } from '../../../../backend/features/badges/shared';
import { useAccountBadgeActionChecks, } from '../../../../ui/features/badge-management/hooks/useAccountBadgeActionChecks';
import { useAccountBadgeReissueAction } from '../../../../ui/features/badge-management/hooks/useAccountBadgeReissueAction';
import { useBadgingTranslation } from '../../../../ui/hooks';
import { showNotificationDialog } from '@juvare/alert-ui/ui/features/alerts';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import { startOfDay } from 'date-fns/startOfDay';
import { useAccountBadgeHistoryOpenAction } from './useAccountBadgeHistoryOpenAction';
import { useAccountBadgeRevokeAction } from './useAccountBadgeRevokeAction';
/**
 * Centralized place for all single Account Badge actions
 */
export function useAccountBadgeActions(params) {
    const { accountBadge, onSuccess } = params;
    const { t } = useBadgingTranslation();
    const accountBadgeStorageClient = useStorageClient(accountBadgeEntityName);
    const checks = useAccountBadgeActionChecks({ accountBadgesSelected: [accountBadge] });
    const { handleAccountBadgeRevoke } = useAccountBadgeRevokeAction(params);
    const { handleReissueAccountBadge } = useAccountBadgeReissueAction(params);
    const { handleAccountBadgeHistoryOpen } = useAccountBadgeHistoryOpenAction(params);
    const handleRequestSuccess = (options) => {
        void onSuccess();
        showAlertSnackbar({
            horizontalPosition: 'center',
            type: 'success',
            verticalPosition: 'bottom',
            autoHideDuration: 2000,
            message: options.message,
        });
    };
    const updateAccountBadge = (data) => __awaiter(this, void 0, void 0, function* () {
        return accountBadgeStorageClient
            .post({
            action: 'partial-update',
            id: accountBadge.id,
            data,
        })
            .then(() => handleRequestSuccess({ message: t('badges.badgeList.editDialog.successMessage') }))
            .catch(handleErrorSnackbar);
    });
    const handleAccountBadgeDistributionStatusEdit = () => {
        const status = accountBadge.columns.status;
        const distributionStatus = accountBadge.columns.distributionStatus;
        const allowedDistributionStatuses = [
            ...getAccountBadgeValidDistributionStatusChanges({
                status,
                distributionStatus,
            }).allowed,
            distributionStatus,
        ].sort((a, b) => accountBadgeDistributionStatuses.indexOf(a) - accountBadgeDistributionStatuses.indexOf(b));
        showFormDialog({
            title: t('badges.badgeDetails.accountBadgeTable.menuItems.changeDistributionStatus.modalTitle', {
                name: accountBadge.columns.accountDisplayName,
            }),
            config: {
                type: 'form.advancedRadio',
                key: 'distributionStatus',
                validate: { required: true },
                values: allowedDistributionStatuses.map((accountBadgeDistributionStatus) => ({
                    value: accountBadgeDistributionStatus,
                    label: t(`badges.badgeDetails.distributionStatuses.${accountBadgeDistributionStatus}`),
                    description: t(`badges.badgeDetails.distributionStatusDescriptions.${accountBadgeDistributionStatus}`),
                })),
            },
            data: { distributionStatus },
            submitLabel: t('common.save'),
            onSubmit: ({ distributionStatus }) => updateAccountBadge({ distributionStatus }),
        });
    };
    const handleAccountBadgeExpirationDateEdit = () => showFormDialog({
        title: t('badges.badgeDetails.accountBadgeTable.menuItems.changeExpirationDate.modalTitle', {
            name: accountBadge.columns.accountDisplayName,
        }),
        config: {
            type: 'form.datePicker',
            label: t('badges.badgeDetails.accountBadgeTable.menuItems.changeExpirationDate.expirationDate'),
            key: 'expiresAt',
            format: 'mm/dd/yyyy',
            validate: { required: true, minDate: startOfDay(new Date()).toISOString() },
        },
        data: {
            expiresAt: new Date(accountBadge.columns.expiresAt).toISOString(),
        },
        onSubmit: ({ expiresAt }) => updateAccountBadge({ expiresAt: Date.parse(expiresAt) }),
    });
    const logBadgeHistoryAction = useTypedPlatformFunction(logBadgeHistoryActionFunctionDef);
    const handleAccountBadgeNotificationSending = () => showNotificationDialog({
        onSubmitSuccess: (_, onClose) => {
            showAlertSnackbar({
                message: t('badges.badgeDetails.accountBadgeTable.notification.sent'),
                type: 'success',
                autoHideDuration: 2000,
            });
            onClose();
            logBadgeHistoryAction({
                accountBadgeIds: [accountBadge.id],
                badgeId: accountBadge.columns.badgeId,
                action: 'accountBadgeNotificationSent',
            }).catch(handleErrorSnackbar);
        },
        mode: 'notification',
        reviewFormHeader: t('badges.badgeDetails.accountBadgeTable.notification.reviewAndSend'),
        wizardHeader: t('badges.badgeDetails.accountBadgeTable.notification.createNotification'),
        partialAlert: {
            recipients: {
                internal: {
                    includeNonMembers: false,
                    accounts: [accountBadge.columns.accountId],
                    groups: [],
                    organizations: [],
                },
            },
        },
    });
    const handleAccountBadgeDistributionStatusChange = (distributionStatus) => {
        void updateAccountBadge({ distributionStatus });
    };
    return Object.assign(Object.assign({}, checks), { handleAccountBadgeDistributionStatusEdit,
        handleAccountBadgeExpirationDateEdit,
        handleAccountBadgeNotificationSending,
        handleAccountBadgeDistributionStatusChange,
        handleAccountBadgeHistoryOpen,
        handleAccountBadgeRevoke,
        handleReissueAccountBadge });
}
